import { Pagination } from "antd";
import { Component } from "react";
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom-v5-compat";

import { cn } from "@btnet/utilities-core";

import { BTSelectItem } from "types/apiResponse/apiResponse";
import { BTLocalStorage } from "types/btStorage";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { getPagingData, type ListEntityType, type PagingData } from "utilities/list/list.types";
import { type IScrollPos } from "utilities/scroll/scroll";
import { type KeyOfOrString } from "utilities/type/PropsOfType";

import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { Hotkey } from "commonComponents/utilities/Hotkey/Hotkey";
import { getHotkeyProps } from "commonComponents/utilities/Hotkey/hotkey.utility";
import { NumberDisplay } from "commonComponents/utilities/NumberDisplay/NumberDisplay";

import "./BTPagination.less";

export interface IPagingState {
    currentPage: number;
    pageSize: number;
}

export interface IBTPaginationHistoryState {
    pagingDataState?: IPagingState;
    /**
     * Stores the scroll position of the page for restoring it when a user navigates back to a list.
     * Currently only supports window level scrollbars via useScrollPositionHistory.
     */
    scrollPosition?: IScrollPos;
    /**
     * Intended to be used to determine if the job context has changed when navigating back to a list.
     * Currently only tracked via useScrollPositionHistory.
     */
    jobIds?: number[];
}

interface IBTPaginationProps extends ITrackingProp {
    /** List id for saving/retrieval */
    entityType: ListEntityType;
    pagingData: PagingData;
    onChange: (updatedPagingData: PagingData) => void;
    resetScrollOnChange?: boolean;
    id: KeyOfOrString;
    className?: string;
    availablePageSizes?: number[];
}

class BTPaginationBase extends Component<IBTPaginationProps> {
    static defaultProps = {
        resetScrollOnChange: true,
        className: "",
        availablePageSizes: [20, 50, 75, 100, 250],
    };
    private resetScrollPosition = () => {
        const { resetScrollOnChange } = this.props;
        if (resetScrollOnChange) {
            window.scrollTo({ left: 0, top: 0 });
        }
    };

    private handleChange = (page: number, pageSize?: number) => {
        const { pagingData, onChange, tracking } = this.props;
        onChange(getPagingData(page, pageSize, pagingData.total));
        this.resetScrollPosition();
        tracking?.trackEvent({
            event: "ButtonClick",
            uniqueId: "pageChange",
        });
    };

    private handlePageSizeChange = (pageSize?: number) => {
        const { pagingData, onChange } = this.props;
        // reset back to page 1 and set page size
        onChange(getPagingData(1, pageSize, pagingData.total));
        this.resetScrollPosition();
        this.setPageSizePreferences(pageSize);
    };

    private setPageSizePreferences = (pageSize?: number) => {
        const { entityType } = this.props;

        const oldPageSizePreferences = BTLocalStorage.get("bt-object-pageSizePreferences");
        const newPageSizePreferences = {
            ...oldPageSizePreferences,
            [entityType]: pageSize,
        };

        BTLocalStorage.set("bt-object-pageSizePreferences", newPageSizePreferences);
    };

    private renderRange = () => {
        const { pageSize, firstRow, lastRow, total } = this.props.pagingData;
        const start = <NumberDisplay value={firstRow} decimalScale={0} />;
        const end = <NumberDisplay value={lastRow < total ? lastRow : total} decimalScale={0} />;
        const totalItems = <NumberDisplay value={total} decimalScale={0} />;
        if (pageSize) {
            return (
                <span className="range">
                    {start}-{end} of {totalItems} items
                </span>
            );
        } else {
            return null;
        }
    };

    private renderPageSizeSelector = () => {
        const { id, availablePageSizes } = this.props;
        const { pageSize, total } = this.props.pagingData;
        if (pageSize && total > availablePageSizes![0]) {
            const pageSizeOptions = availablePageSizes!.map(
                (size) =>
                    new BTSelectItem({
                        key: size,
                        value: size,
                        name: `${size} / page`,
                        selected: pageSize === size,
                    }),
            );

            return (
                <BTSelect
                    id={id}
                    data-testid={`${id}-pageSize`}
                    className="PageSizeSelect"
                    treeData={pageSizeOptions}
                    value={pageSize}
                    onChange={(field: string, value: number) => this.handlePageSizeChange(value)}
                    onBlur={() => {}}
                    getPopupContainer={(trigger) => trigger.parentNode}
                    showSearch={false}
                />
            );
        } else {
            return null;
        }
    };

    private getItemRender = (
        page: number,
        type: "page" | "prev" | "next" | "jump-prev" | "jump-next",
        originalElement: React.ReactElement<HTMLElement>,
    ) => {
        let element = originalElement;
        if (type === "prev") {
            const newPage = page--;
            element = (
                <Hotkey onCommand={() => this.handleChange(newPage)} {...getHotkeyProps("navLeft")}>
                    {originalElement}
                </Hotkey>
            );
        } else if (type === "next") {
            const newPage = page++;
            element = (
                <Hotkey
                    onCommand={() => this.handleChange(newPage)}
                    {...getHotkeyProps("navRight")}
                >
                    {originalElement}
                </Hotkey>
            );
        }
        return element;
    };

    render() {
        const {
            pagingData: { currentPage, pageSize, total },
            entityType,
            className,
            id,
        } = this.props;
        const divClassnames = cn("BTPagination", className);

        return (
            <div className={divClassnames} id={id} data-testid={id}>
                {this.renderRange()}
                <Pagination
                    className="navigator"
                    simple
                    defaultPageSize={getDefaultPageSize(entityType)}
                    hideOnSinglePage
                    current={currentPage}
                    pageSize={pageSize}
                    total={total}
                    onChange={this.handleChange}
                    onShowSizeChange={this.handlePageSizeChange}
                    itemRender={this.getItemRender}
                />
                {this.renderPageSizeSelector()}
            </div>
        );
    }
}

export const BTPagination = track({ component: "Pagination Controls" })(BTPaginationBase);

/**
 * This is BTPagination is designed for a full page experience (list pages, full page detail views etc).
 *
 * @example
 * const state = useHistoryState<IListPageState>();
 * const paging = usePagination(state?.pagingDataState, entityType);
 * return <BTPaginationHistory />
 */
export const BTPaginationHistory = (props: Omit<IBTPaginationProps, "onChange">) => {
    const location = useLocation<IBTPaginationHistoryState>();
    const navigate = useNavigate();

    const handleOnChange = (updatedPagingData: PagingData) => {
        const newState: IBTPaginationHistoryState = {
            pagingDataState: updatedPagingData,
            scrollPosition: {
                posX: 0,
                posY: 0,
            },
        };
        navigate(location.pathname + location.search, {
            replace: true,
            state: {
                ...(typeof location.state === "object" ? location.state : {}),
                ...newState,
            },
        });
    };

    return <BTPagination {...props} onChange={handleOnChange} />;
};

export default BTPagination;

export function getDefaultPageSize(entityType: ListEntityType, defaultValue: number = 50) {
    const pageSizePreferences = BTLocalStorage.get("bt-object-pageSizePreferences");

    return pageSizePreferences[entityType] ?? defaultValue;
}
